// WebSocket клиента (E01-T09, tech/03 §19): рукопожатие {t:'auth'}, пульс ping/pong (60 с без пинга — обрыв),
// переподключение 1/2/4/8/15 с ±30%. События сервера проверяются схемой WsServerMessage договора.
// Каждая смена состояния — событие ws_state (tech/09 §3). В режиме фикстур транспорт — мок-сервер.
import { signal } from '@preact/signals';
import { WS_PATH, WS_PONG_TIMEOUT_MS, WS_RECONNECT_DELAYS_MS, WsServerMessage, type WsEvent } from '@garages/contract';
import { track } from '../analytics/analytics.js';

type Handler = (ev: WsEvent) => void;
const handlers = new Set<Handler>();

export type WsStateName = 'open' | 'closed' | 'reconnect' | 'fallback_poll';
export const wsState = signal<WsStateName>('closed');

function setState(state: WsStateName, attempt: number) {
  if (wsState.value === state) return;
  wsState.value = state;
  track('ws_state', { state, attempt });
}

export function onWs(h: Handler): () => void {
  handlers.add(h);
  return () => handlers.delete(h);
}

/** Последний полученный seq — точка продолжения для GET /sync?since= (03 §19). */
export let lastSeq = 0;

export function emitWs(raw: unknown): void {
  const m = WsServerMessage.safeParse(raw);
  if (!m.success || !('seq' in m.data)) return;
  const ev = m.data as WsEvent;
  if (ev.seq <= lastSeq && lastSeq > 0) return; // повтор из опроса после переподключения
  lastSeq = Math.max(lastSeq, ev.seq);
  for (const h of [...handlers]) h(ev);
}

/** Задержка переподключения: 1, 2, 4, 8, 15 с, затем 15 с; разброс ±30% (03 §19). */
export function reconnectDelay(attempt: number, rand: () => number = Math.random): number {
  const base = WS_RECONNECT_DELAYS_MS[Math.min(attempt, WS_RECONNECT_DELAYS_MS.length - 1)]!;
  return Math.round(base * (0.7 + rand() * 0.6));
}

export interface WsDeps {
  token: () => string | null;
  url?: string;
  make?: (url: string) => WebSocket;
  /** После 3 неудач подряд — запасной опрос GET /sync раз в 10 с (03 §18–§19, fallback_poll). */
  maxAttemptsBeforePoll?: number;
  /** GET /sync?since=; события те же конверты, что в WS. */
  poll?: (since: number) => Promise<{ events: unknown[]; lastSeq: number; gap: boolean }>;
  /** `gap: true` — пропуск больше 15 мин: нужен GET /bootstrap. */
  onGap?: () => void;
  pollMs?: number;
}

/** Соединение на всё время сессии. Без токена ждёт входа (проверка раз в секунду), токен берётся при каждом подключении. */
export function connectWs(d: WsDeps): () => void {
  const url = d.url ?? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${WS_PATH}`;
  const make = d.make ?? ((u: string) => new WebSocket(u));
  let ws: WebSocket | null = null;
  let attempt = 0;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pulse: ReturnType<typeof setTimeout> | null = null;
  let poller: ReturnType<typeof setInterval> | null = null;
  const stopPoll = () => { if (poller) clearInterval(poller); poller = null; };
  const startPoll = () => {
    if (poller || !d.poll) return;
    const tick = async () => {
      try {
        const r = await d.poll!(lastSeq);
        for (const e of r.events) emitWs(e);
        lastSeq = Math.max(lastSeq, r.lastSeq);
        if (r.gap) d.onGap?.();
      } catch { /* сеть — следующий тик */ }
    };
    void tick();
    poller = setInterval(() => { void tick(); }, d.pollMs ?? 10_000);
  };
  const later = (fn: () => void, ms: number) => { timer = setTimeout(fn, ms); };
  const alive = () => {
    if (pulse) clearTimeout(pulse);
    pulse = setTimeout(() => ws?.close(), WS_PONG_TIMEOUT_MS);
  };
  const open = () => {
    if (stopped) return;
    const t = d.token();
    if (!t) { later(open, 1000); return; }
    try { ws = make(url); } catch { scheduleReconnect(); return; }
    ws.onopen = () => { attempt = 0; stopPoll(); setState('open', 0); alive(); ws?.send(JSON.stringify({ t: 'auth', token: t })); };
    ws.onmessage = (e) => {
      alive();
      let data: unknown;
      try { data = JSON.parse(String(e.data)); } catch { return; }
      const m = data as { t?: string };
      if (m.t === 'ping') { ws?.send(JSON.stringify({ t: 'pong' })); return; }
      if (m.t === 'reauth') { ws?.close(); return; }
      emitWs(data);
    };
    ws.onclose = () => { if (pulse) clearTimeout(pulse); if (!stopped) scheduleReconnect(); };
    ws.onerror = () => { /* за ним придёт onclose */ };
  };
  const scheduleReconnect = () => {
    const n = attempt++;
    const poll = n + 1 >= (d.maxAttemptsBeforePoll ?? 3);
    setState(poll ? 'fallback_poll' : 'reconnect', n + 1);
    if (poll) startPoll(); // WS пробуем дальше раз в 15 с, опрос держит данные свежими
    later(open, reconnectDelay(n));
  };
  // Связь вернулась — пробуем сразу, не дожидаясь задержки.
  const onOnline = () => { if (wsState.value !== 'open') { if (timer) clearTimeout(timer); attempt = 0; open(); } };
  globalThis.addEventListener?.('online', onOnline);
  open();
  return () => {
    stopped = true;
    globalThis.removeEventListener?.('online', onOnline);
    if (timer) clearTimeout(timer);
    if (pulse) clearTimeout(pulse);
    stopPoll();
    ws?.close();
    setState('closed', attempt);
  };
}
